import { Injectable, OnDestroy, inject } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';
import { MapService } from '../../../core/map/map.service';
import { MapSource, SourceItem } from '../../../core/map/models/map-source';
import {
    CropMapBoundaryLayerViewModel,
    CropMapYearDataViewModel,
} from '../../../proxy/proxies';
import { CityModel, DistrictModel } from '../../../core/models/config.models';
import { inquiryToAsciiUpper, inquiryToDisplayName } from '../inquiry-text.util';

type FieldHoverHandler = {
    layerId: string;
    onMove: (e: mapboxgl.MapLayerMouseEvent) => void;
    onLeave: (e: mapboxgl.MapLayerMouseEvent) => void;
    onClick?: (e: mapboxgl.MapLayerMouseEvent) => void;
};

export type InquiryFieldInteractionHandlers = {
    onFieldHover: (props: Record<string, unknown>, lngLat: mapboxgl.LngLat) => void;
    onFieldLeave: () => void;
    /** When set, field polygons become clickable (e.g. field observation mode). */
    onFieldClick?: (props: Record<string, unknown>, lngLat: mapboxgl.LngLat) => void;
};

@Injectable({ providedIn: 'root' })
export class InquiryMapLayersService implements OnDestroy {
    private readonly mapService = inject(MapService);

    private sourceList: MapSource[] = [];
    private cityLayer: MapSource | undefined;
    private districtLayer: MapSource | undefined;
    private draw: MapboxDraw | undefined;

    private cityFeatures: mapboxgl.MapboxGeoJSONFeature[] = [];
    private cityQueryInterval: ReturnType<typeof setInterval> | undefined;
    private fieldHandlers: FieldHoverHandler[] = [];

    private isFitted = false;

    cityBoundaryLayer!: CropMapBoundaryLayerViewModel;
    districtBoundaryLayer!: CropMapBoundaryLayerViewModel;
    fieldLayerCityProperty = 'city_id';
    fieldLayerDistrictProperty = 'district_id';

    private lastAuthorizedCities: CityModel[] = [];
    private lastAuthorizedDistricts: DistrictModel[] = [];

    private fieldInteraction: InquiryFieldInteractionHandlers = {
        onFieldHover: () => {},
        onFieldLeave: () => {},
    };

    setFieldInteractionHandlers(handlers: InquiryFieldInteractionHandlers): void {
        this.fieldInteraction = handlers;
    }

    ngOnDestroy(): void {
        this.dispose();
    }

    /** Clear inquiry field handlers and draw (boundary/field layers cleared via MapService). */
    dispose(): void {
        this.clearCityQueryInterval();
        this.teardownFieldHandlers();
        this.deleteDrawing();
    }

    /** Attach hover handlers to the unified GeoJSON field boundary layer. */
    registerFieldLayerHandlers(layerId: string): void {
        const m = this.map;
        if (!m) return;

        this.teardownFieldHandlers();
        const options = this.fieldInteraction;

        const onMove = (e: mapboxgl.MapLayerMouseEvent) => {
            const properties = e.features?.[0]?.properties as Record<string, unknown> | undefined;
            if (!properties) return;

            const cityProp = properties[this.fieldLayerCityProperty];
            if (
                !this.lastAuthorizedCities.some(
                    (c) => String(c.cityId) === String(cityProp) || Number(c.cityId) === Number(cityProp),
                )
            ) {
                return;
            }

            const zoom = m.getZoom();
            if (zoom > 12 && zoom < 20) {
                options.onFieldHover(properties, e.lngLat);
            } else {
                options.onFieldLeave();
            }
        };

        const onLeave = () => options.onFieldLeave();

        m.on('mousemove', layerId, onMove);
        m.on('mouseleave', layerId, onLeave);
        this.fieldHandlers.push({ layerId, onMove, onLeave });
    }

    get map(): mapboxgl.Map | undefined {
        return this.mapService.map;
    }

    flyTo(center: { lng: number; lat: number }, zoom: number): void {
        this.mapService.flyTo(center, zoom);
    }

    resetFocusDefault(): void {
        this.flyTo({ lat: 39.94477880824295, lng: 34.11754412215642 }, 6);
    }

    safeFitBounds(bounds: mapboxgl.LngLatBoundsLike, options?: mapboxgl.FitBoundsOptions): boolean {
        const m = this.map;
        if (m && bounds) {
            m.fitBounds(bounds, options);
            return true;
        }
        return false;
    }

    setAuthorizedGeo(
        cities: CityModel[],
        districts: DistrictModel[],
    ): void {
        this.lastAuthorizedCities = cities;
        this.lastAuthorizedDistricts = districts;
    }

    resetMap(): void {
        this.clearCityQueryInterval();
        this.teardownFieldHandlers();
        this.removeAllLayers();
        this.removeAllSources();
        this.removeCityLayers();
        this.removeDistrictLayers();
        this.isFitted = false;
    }

    fitCountryBounds(mapboxLayerConfig: {
        longitude: { min: number; max: number };
        latitude: { min: number; max: number };
    }): void {
        this.safeFitBounds([
            [mapboxLayerConfig.longitude.min, mapboxLayerConfig.latitude.min],
            [mapboxLayerConfig.longitude.max, mapboxLayerConfig.latitude.max],
        ] as mapboxgl.LngLatBoundsLike);
    }

    setBoundaryMetadataFromYearData(selectedLayerData: CropMapYearDataViewModel): void {
        this.cityBoundaryLayer = selectedLayerData.boundaryLayers?.find((e) => e.layerType === 'City')!;
        this.districtBoundaryLayer = selectedLayerData.boundaryLayers?.find((e) => e.layerType === 'District')!;
        this.cityBoundaryLayer['property'] = this.cityBoundaryLayer.property ?? 'CityName';
        this.districtBoundaryLayer['property'] = this.districtBoundaryLayer.property ?? 'DistrictName';
    }

    wireSourcedataForCityFit(): void {
        const m = this.map;
        if (!m) return;
        m.on('sourcedata', () => {
            if (this.cityLayer && m.getSource(this.cityLayer.Id) && m.isSourceLoaded(this.cityLayer.Id)) {
                this.flyToForCities();
            }
        });
    }

    executeFilter(selectedLayerData: CropMapYearDataViewModel): void {
        this.resetMap();
        setTimeout(() => {
            this.loadCityBoundaryLayerOnMap();
            this.loadFieldLayersOnMap(selectedLayerData, this.fieldInteraction);
        }, 1000);
    }

    loadCityBoundaryLayerOnMap(selectedCityAscii?: string | null): void {
        const m = this.map;
        if (!m || !this.cityBoundaryLayer) return;

        const cityFilter: mapboxgl.FilterSpecification = selectedCityAscii?.length
            ? (['!=', ['get', 'city'], selectedCityAscii] as mapboxgl.FilterSpecification)
            : (['!=', ['get', 'city'], ''] as mapboxgl.FilterSpecification);

        const cName = this.cityBoundaryLayer.layerName ?? '';
        const cUrl = this.cityBoundaryLayer.url ?? '';
        const citySource: SourceItem = {
            Id: cName + '_source',
            LayerId: cName + '_layer',
            LayerName: cName,
            Url: cUrl,
            Filter: cityFilter,
        };
        this.addCityBoundaryLayer(citySource);
        this.applyCityAuthorizationFilter(citySource.LayerId);
    }

    loadFieldLayersOnMap(
        mapboxLayersOfSelectedYear: CropMapYearDataViewModel,
        options: InquiryFieldInteractionHandlers,
    ): void {
        const m = this.map;
        if (!m) return;

        this.fieldLayerCityProperty = mapboxLayersOfSelectedYear.fieldLayer?.cityProperty ?? 'city_id';
        this.fieldLayerDistrictProperty = mapboxLayersOfSelectedYear.fieldLayer?.districtProperty ?? 'district_id';

        this.teardownFieldHandlers();

        for (const layer of mapboxLayersOfSelectedYear.fieldLayer?.fieldLayerDetails ?? []) {
            const lName = layer.layerName ?? '';
            const lUrl = layer.url ?? '';
            const fieldLayerConfig: SourceItem = {
                Id: lName + '_source',
                LayerId: lName + '_layer',
                LayerName: lName,
                Url: lUrl,
            };

            this.addSource(fieldLayerConfig);
            this.addLayer(fieldLayerConfig);
            this.hideUnauthorizedLayers(fieldLayerConfig.LayerId);

            const onMove = (e: mapboxgl.MapLayerMouseEvent) => {
                const properties = e.features?.[0]?.properties as Record<string, unknown> | undefined;
                if (!properties) return;

                const cityProp = properties[this.fieldLayerCityProperty];
                if (
                    !this.lastAuthorizedCities.some(
                        (c) => String(c.cityId) === String(cityProp) || Number(c.cityId) === Number(cityProp),
                    )
                ) {
                    return;
                }

                const zoom = m.getZoom();
                if (zoom > 12 && zoom < 20) {
                    options.onFieldHover(properties, e.lngLat);
                } else {
                    options.onFieldLeave();
                }
            };

            const onLeave = () => options.onFieldLeave();

            const createClickHandler = () => {
                const onClick = (e: mapboxgl.MapLayerMouseEvent) => {
                    if (!options.onFieldClick) return;
                    const properties = e.features?.[0]?.properties as Record<string, unknown> | undefined;
                    if (!properties) return;

                    const cityProp = properties[this.fieldLayerCityProperty];
                    if (
                        !this.lastAuthorizedCities.some(
                            (c) => String(c.cityId) === String(cityProp) || Number(c.cityId) === Number(cityProp),
                        )
                    ) {
                        return;
                    }

                    const zoom = m.getZoom();
                    if (zoom > 12 && zoom < 20) {
                        options.onFieldClick!(properties, e.lngLat);
                    }
                };
                return onClick;
            };

            const onClickHandler = options.onFieldClick ? createClickHandler() : undefined;

            m.on('mousemove', fieldLayerConfig.LayerId, onMove);
            m.on('mouseleave', fieldLayerConfig.LayerId, onLeave);
            if (onClickHandler) {
                m.on('click', fieldLayerConfig.LayerId, onClickHandler);
            }
            this.fieldHandlers.push({
                layerId: fieldLayerConfig.LayerId,
                onMove,
                onLeave,
                onClick: onClickHandler,
            });
        }
    }

    /**
     * @param cityLabel display name from lookup (e.g. Turkish spelling)
     */
    onSelectCity(cityLabel: string | null): void {
        const m = this.map;
        if (!m || !this.cityBoundaryLayer || !this.districtBoundaryLayer) return;

        if (cityLabel == null) {
            this.resetFocusDefault();
            return;
        }

        const ascii = inquiryToAsciiUpper(cityLabel.toUpperCase());

        const cName = this.cityBoundaryLayer.layerName ?? '';
        const cUrl = this.cityBoundaryLayer.url ?? '';
        const dName = this.districtBoundaryLayer.layerName ?? '';
        const dUrl = this.districtBoundaryLayer.url ?? '';

        const citySource: SourceItem = {
            Id: cName + '_source',
            LayerId: cName + '_layer',
            LayerName: cName,
            Url: cUrl,
        };

        const districtSource: SourceItem = {
            Id: dName + '_source',
            LayerId: dName + '_layer',
            LayerName: dName,
            Url: dUrl,
            Filter: ['==', ['get', 'city'], ascii] as mapboxgl.FilterSpecification,
        };

        this.addDistrictBoundaryLayer(districtSource);
        m.setFilter(citySource.LayerId, ['!=', ['get', 'city'], ascii] as mapboxgl.FilterSpecification);
        this.applyCityAuthorizationFilter(citySource.LayerId);
        this.applyDistrictAuthorizationFilter(districtSource.LayerId, ascii);
        this.zoomInTheSelectedCity(inquiryToDisplayName(cityLabel));
    }

    focusFromGeocode(center: [number, number]): void {
        this.flyTo({ lng: center[0], lat: center[1] }, 8);
    }

    showInquiryResult(result: { geometry?: { type?: string; coordinates?: number[][][] } }): void {
        if (!result?.geometry?.coordinates?.[0]) return;
        const ring = result.geometry.coordinates[0];
        const coordinates: number[][] = [];
        for (const t of ring) {
            const dup = coordinates.some((a) => a[0] === t[0] && a[1] === t[1]);
            if (!dup) coordinates.push(t);
        }
        if (coordinates.length) {
            coordinates.push(coordinates[0]);
            (result.geometry as { coordinates: number[][][] }).coordinates[0] = coordinates;
        }
        this.initiateParcelDrawing(result as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>, false);
    }

    onChangeYearOrSeason(selectedLayerData: CropMapYearDataViewModel | undefined): void {
        if (!selectedLayerData) return;
        this.executeFilter(selectedLayerData);
    }

    deleteDrawing(): void {
        const m = this.map;
        if (this.draw && m) {
            try {
                this.draw.deleteAll();
                this.draw.trash();
                m.removeControl(this.draw);
            } catch {
                /* already removed */
            }
            this.draw = undefined;
        }
    }

    private clearCityQueryInterval(): void {
        if (this.cityQueryInterval) {
            clearInterval(this.cityQueryInterval);
            this.cityQueryInterval = undefined;
        }
    }

    private teardownFieldHandlers(): void {
        const m = this.map;
        if (m) {
            for (const h of this.fieldHandlers) {
                m.off('mousemove', h.layerId, h.onMove);
                m.off('mouseleave', h.layerId, h.onLeave);
                if (h.onClick) {
                    m.off('click', h.layerId, h.onClick);
                }
            }
        }
        this.fieldHandlers = [];
    }

    private addBoundaryLayer(item: SourceItem, type: 'city' | 'district'): void {
        const map = this.map;
        if (!map) return;

        if (!map.getSource(item.Id)) {
            map.addSource(item.Id, {
                type: 'vector',
                url: item.Url,
            });
        }

        if (!map.getLayer(item.LayerId)) {
            const layer: mapboxgl.FillLayer = {
                id: item.LayerId,
                type: 'fill',
                source: item.Id,
                'source-layer': item.LayerName,
                paint: {
                    'fill-outline-color': type === 'city' ? '#FFFFFF' : '#d3a453',
                    'fill-color': '#000000',
                    'fill-opacity': type === 'city' ? 0.6 : 0.4,
                },
                filter: item.Filter as mapboxgl.FilterSpecification,
            };
            map.addLayer(layer);
        }
    }

    private addCityBoundaryLayer(item: SourceItem): void {
        this.removeCityLayers();
        if (!item.Filter) {
            item.Filter = ['!=', ['get', 'city'], ''] as mapboxgl.FilterSpecification;
        }
        this.addBoundaryLayer(item, 'city');
        this.cityLayer = {
            Id: item.Id,
            Source: item.Url,
            Layers: [{ layerId: item.LayerId, layerName: item.LayerName }],
        };
        this.getCityBoundaryLayerFeatures(item);
    }

    private addDistrictBoundaryLayer(item: SourceItem): void {
        this.removeDistrictLayers();
        if (!item.Filter) {
            item.Filter = ['!=', ['get', 'city'], ''] as mapboxgl.FilterSpecification;
        }
        this.addBoundaryLayer(item, 'district');
        this.districtLayer = {
            Id: item.Id,
            Source: item.Url,
            Layers: [{ layerId: item.LayerId, layerName: item.LayerName }],
        };
    }

    private getCityBoundaryLayerFeatures(item: SourceItem): void {
        this.clearCityQueryInterval();
        this.cityFeatures = [];
        let count = 0;
        this.cityQueryInterval = setInterval(() => {
            const map = this.map;
            if (map?.getSource(item.Id) && map.isSourceLoaded(item.Id)) {
                if (count < 4) {
                    const feats = map.querySourceFeatures(item.Id, { sourceLayer: item.LayerName });
                    if (feats && this.cityFeatures.length < feats.length) {
                        this.cityFeatures = [...feats];
                    }
                    count += 1;
                } else {
                    this.clearCityQueryInterval();
                }
            }
        }, 1500);
    }

    private applyCityAuthorizationFilter(layerId: string): void {
        const m = this.map;
        if (!m) return;
        const prop = String(this.cityBoundaryLayer.property);
        const filters: unknown[] = ['any'];
        for (const city of this.lastAuthorizedCities) {
            filters.push(['==', ['get', prop], city.cityId]);
        }
        if (filters.length > 1) {
            m.setFilter(layerId, filters as mapboxgl.FilterSpecification);
            const filterExpression: mapboxgl.ExpressionSpecification = ['case', filters, 0.6, 0];
            m.setPaintProperty(layerId, 'fill-opacity', filterExpression);
        }
    }

    private applyDistrictAuthorizationFilter(layerId: string, selectedCityKey: string | number): void {
        const m = this.map;
        if (!m) return;
        const cityProp = String(this.cityBoundaryLayer.property);
        const distProp = String(this.districtBoundaryLayer.property);
        const filters: unknown[] = ['any'];
        filters.push(['==', ['get', cityProp], selectedCityKey]);
        const cityIdNum = Number(selectedCityKey);
        const districtsForCity = Number.isFinite(cityIdNum)
            ? this.lastAuthorizedDistricts.filter((d) => d.cityId === cityIdNum)
            : this.lastAuthorizedDistricts;
        for (const district of districtsForCity) {
            filters.push(['==', ['get', distProp], district.districtId]);
        }
        if (filters.length > 1) {
            m.setFilter(layerId, filters as mapboxgl.FilterSpecification);
            const filterExpression: mapboxgl.ExpressionSpecification = ['case', filters, 0.6, 0];
            m.setPaintProperty(layerId, 'fill-opacity', filterExpression);
        }
    }

    private removeCityLayers(): void {
        if (!this.cityLayer) return;
        const m = this.map;
        for (const e of this.cityLayer.Layers ?? []) {
            if (m?.getLayer(e.layerId)) m.removeLayer(e.layerId);
        }
        if (m?.getSource(this.cityLayer.Id)) m.removeSource(this.cityLayer.Id);
        this.cityLayer = undefined;
    }

    private removeDistrictLayers(): void {
        if (!this.districtLayer) return;
        const m = this.map;
        for (const e of this.districtLayer.Layers ?? []) {
            if (m?.getLayer(e.layerId)) m.removeLayer(e.layerId);
        }
        if (m?.getSource(this.districtLayer.Id)) m.removeSource(this.districtLayer.Id);
        this.districtLayer = undefined;
    }

    private removeAllSources(): void {
        const m = this.map;
        for (const e of this.sourceList) {
            if (m?.getSource(e.Id)) m.removeSource(e.Id);
        }
        this.sourceList = [];
    }

    private removeAllLayers(): void {
        const m = this.map;
        for (const e of this.sourceList) {
            for (const layer of e.Layers ?? []) {
                if (m?.getLayer(layer.layerId)) m.removeLayer(layer.layerId);
            }
        }
    }

    private addSource(item: SourceItem): void {
        const m = this.map;
        if (!m) return;
        if (!m.getSource(item.Id)) {
            this.sourceList.push({ Id: item.Id, Source: item.Url, Layers: [] });
            m.addSource(item.Id, { type: 'vector', url: item.Url });
        }
    }

    private addLayer(item: SourceItem): void {
        const m = this.map;
        if (!m) return;
        if (!m.getLayer(item.LayerId)) {
            const bucket = this.sourceList.find((e) => e.Id === item.Id);
            if (bucket && !bucket.Layers) bucket.Layers = [];
            bucket?.Layers?.push({ layerId: item.LayerId, layerName: item.LayerName });
            m.addLayer({
                id: item.LayerId,
                type: 'fill',
                source: item.Id,
                'source-layer': item.LayerName,
                paint: {
                    'fill-outline-color': '#d3a453',
                    'fill-color': '#000000',
                    'fill-opacity': 0.8,
                },
            });
        }
    }

    private hideUnauthorizedLayers(layerId: string): void {
        const m = this.map;
        if (!m) return;
        const filters: unknown[] = ['any'];
        for (const district of this.lastAuthorizedDistricts) {
            filters.push(['==', ['get', this.fieldLayerDistrictProperty], district.districtId]);
        }
        if (filters.length > 1) {
            const filterExpression: mapboxgl.ExpressionSpecification = ['case', filters, 1, 0];
            m.setPaintProperty(layerId, 'fill-opacity', filterExpression);
        }
    }

    private flyToForCities(): void {
        if (this.isFitted || !this.cityLayer) return;
        const m = this.map;
        const sourceData = m?.getSource(this.cityLayer.Id) as { bounds?: [number, number, number, number] };
        const sourceBounds = sourceData?.bounds;
        if (sourceBounds?.length === 4) {
            this.safeFitBounds([
                [sourceBounds[0], sourceBounds[1]],
                [sourceBounds[2], sourceBounds[3]],
            ]);
        }
        this.isFitted = true;
    }

    private zoomInTheSelectedCity(param: string | number): void {
        const prop = String(this.cityBoundaryLayer.property);
        const targetFeature = this.cityFeatures.find(
            (feature) => feature.properties?.[prop] === param,
        );
        if (targetFeature) {
            const bbox = turf.bbox(targetFeature as unknown as GeoJSON.Feature);
            this.safeFitBounds(bbox as mapboxgl.LngLatBoundsLike, {
                padding: { top: 10, bottom: 25, left: 400, right: 5 },
                duration: 2000,
            });
        }
    }

    private initiateParcelDrawing(
        geojson: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>,
        drawEditable = true,
    ): void {
        const m = this.map;
        if (!m) return;

        if (this.draw) {
            this.deleteDrawing();
        }
        const g = geojson.geometry;
        if (!g) return;
        let coords: number[][] | undefined;
        if (g.type === 'Polygon') {
            coords = g.coordinates[0];
        } else if (g.type === 'MultiPolygon') {
            coords = g.coordinates[0]?.[0];
        }
        if (!coords?.length) return;
        this.draw = this.addDrawablePolygon(geojson as unknown as { geometry?: GeoJSON.Geometry });

        let minLng = Infinity;
        let minLat = Infinity;
        let maxLng = -Infinity;
        let maxLat = -Infinity;
        for (const coord of coords) {
            const [lng, lat] = coord;
            if (lng < minLng) minLng = lng;
            if (lat < minLat) minLat = lat;
            if (lng > maxLng) maxLng = lng;
            if (lat > maxLat) maxLat = lat;
        }
        const bbox: mapboxgl.LngLatBoundsLike = [minLng, minLat, maxLng, maxLat];
        this.safeFitBounds(bbox, {
            padding: { top: 10, bottom: 25, left: 150, right: 5 },
            duration: 2000,
        });
        void drawEditable;
    }

    private addDrawablePolygon(geojson: { geometry?: GeoJSON.Geometry }): MapboxDraw {
        const m = this.map!;
        const draw = new MapboxDraw({
            displayControlsDefault: false,
            touchEnabled: true,
            boxSelect: false,
            userProperties: true,
            controls: {},
            styles: parcelDrawStyles,
            defaultMode: 'simple_select',
        } as ConstructorParameters<typeof MapboxDraw>[0]);
        m.addControl(draw);
        const geo = geojson.geometry ? geojson.geometry : (geojson as unknown as GeoJSON.Geometry);
        draw.add({ type: 'Feature', geometry: geo, properties: {} } as GeoJSON.Feature);
        return draw;
    }
}

// Mapbox Draw uses partial layer specs (no map `source`); keep untyped for Draw compatibility.
const parcelDrawStyles: Record<string, unknown>[] = [
    {
        id: 'gl-draw-line',
        type: 'line',
        filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#fff', 'line-width': 2 },
    },
    {
        id: 'gl-draw-polygon-fill',
        type: 'fill',
        filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
        paint: {
            'fill-color': '#2cc705',
            'fill-outline-color': '#fbfdfd',
            'fill-opacity': 0.3,
        },
    },
    {
        id: 'gl-draw-polygon-midpoint',
        type: 'circle',
        filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
        paint: { 'circle-radius': 10, 'circle-color': '#fff' },
    },
    {
        id: 'gl-draw-polygon-stroke-active',
        type: 'line',
        filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#fff', 'line-width': 5, 'line-dasharray': [3, 3] },
    },
    {
        id: 'gl-draw-polygon-and-line-vertex-halo-active',
        type: 'circle',
        filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
        paint: { 'circle-radius': 5, 'circle-color': '#FFF' },
    },
    {
        id: 'gl-draw-polygon-and-line-vertex-active',
        type: 'circle',
        filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
        paint: { 'circle-radius': 10, 'circle-color': '#fff' },
    },
    {
        id: 'gl-draw-line-static',
        type: 'line',
        filter: ['all', ['==', '$type', 'LineString'], ['==', 'mode', 'static']],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#000', 'line-width': 3 },
    },
    {
        id: 'gl-draw-polygon-fill-static',
        type: 'fill',
        filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
        paint: {
            'fill-color': '#fff',
            'fill-outline-color': '#fff',
            'fill-opacity': 0.4,
        },
    },
    {
        id: 'gl-draw-polygon-stroke-static',
        type: 'line',
        filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#000', 'line-width': 9 },
    },
];
